import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { TestHierarchyNodeType } from "node-library/dist/constants";
import {
	FrontendExpectationInputState,
	FrontendExpectationInputValue,
} from "node-library/dist/frontendInputStates";
import {
	isSelectableTestNode,
	isTestOptionGroup,
} from "node-library/dist/helpers/testHierarchyNodeHelper";
import {
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
	FrontendCompanyTestExpectationField,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestPackage,
	FrontendCompanyTestResultField,
	FrontendCompanyTestResultUnits,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import React from "react";

export function getEnabledHierarchyNodeTree(
	selectedNodeIds: Array<string>,
	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>,
	checkingForTestGroupNodeIds?: Array<string>, // optional
	checkingForTestNodeIds?: Array<string>, // optional
): TestHierarchyTreeNode {
	const rootNode: TestHierarchyTreeNode = {
		...(
			hierarchyNodeParentMap.get(
				"root",
			) as Array<FrontendCompanyTestHierarchyNode>
		)[0],
		children: [],
	};

	let children: Array<FrontendCompanyTestHierarchyNode> =
		hierarchyNodeParentMap.get(rootNode.nodeId) || [];

	children = children.sort((a, b) => a.nodeDisplayOrder - b.nodeDisplayOrder);

	const converted: Array<TestHierarchyTreeNode> = children
		.map((child) => {
			return getEnabledHierarchyNodeTreeHelper(
				selectedNodeIds,
				hierarchyNodeParentMap,
				child,
				checkingForTestGroupNodeIds,
				checkingForTestNodeIds,
				false, // initial ancestor match
			);
		})
		.filter((child) => child != null) as Array<TestHierarchyTreeNode>;

	rootNode.children = converted;
	return rootNode;
}

function getEnabledHierarchyNodeTreeHelper(
	selectedNodeIds: Array<string>,
	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>,
	currentNode: FrontendCompanyTestHierarchyNode,
	checkingForTestGroupNodeIds?: Array<string>,
	checkingForTestNodeIds?: Array<string>, // optional

	ancestorMatched: boolean = false,
): TestHierarchyTreeNode | null {
	//check the test group nodes
	let currentMatched =
		currentNode.nodeType == TestHierarchyNodeType.TEST_GROUP &&
		checkingForTestGroupNodeIds
			? checkingForTestGroupNodeIds?.includes(currentNode.nodeId)
			: true;

	//check the test nodes
	currentMatched &&=
		currentNode.nodeType == TestHierarchyNodeType.TEST && checkingForTestNodeIds
			? checkingForTestNodeIds.includes(currentNode.nodeId)
			: true;

	const includeThisSubtree = currentMatched || ancestorMatched;

	if (
		isSelectableTestNode(currentNode) &&
		!selectedNodeIds.includes(currentNode.nodeId)
	) {
		return null;
	}

	if (!includeThisSubtree) {
		return null;
	}

	let children: Array<FrontendCompanyTestHierarchyNode> =
		hierarchyNodeParentMap.get(currentNode.nodeId) || [];

	if (
		(isTestOptionGroup(currentNode) ||
			currentNode.nodeType == TestHierarchyNodeType.TEST_GROUP) &&
		!children.some((child) => selectedNodeIds.includes(child.nodeId))
	) {
		return null;
	}

	children = children.sort((a, b) => a.nodeDisplayOrder - b.nodeDisplayOrder);

	const treeNode: TestHierarchyTreeNode = { ...currentNode, children: [] };

	const converted: Array<TestHierarchyTreeNode> = children
		.map((child) => {
			return getEnabledHierarchyNodeTreeHelper(
				selectedNodeIds,
				hierarchyNodeParentMap,
				child,
				checkingForTestGroupNodeIds,
				checkingForTestNodeIds,
				includeThisSubtree,
			);
		})
		.filter((child) => child != null) as Array<TestHierarchyTreeNode>;

	treeNode.children = converted;

	// Only return the node if it's part of the allowed subtree
	return treeNode;
}

export function renderEnabledTestInfoSection(
	rootNode: TestHierarchyTreeNode,
	typographyClassname: string,
	fieldsHavingExpectations: FrontendCompanyTestResultField[],
	expectationInputs: FrontendExpectationInputState,
	expectationFieldIdToNameMap: Map<string, string>,
	companyTestResultUnits: Array<FrontendCompanyTestResultUnits>,
	hierarchyNodeMappingPackages: FrontendCompanyResultFieldHierarchyNodeMappingPackage[],
): Array<React.ReactNode> {
	const result: Array<React.ReactNode> = rootNode.children
		.filter((group) => group.nodeType === TestHierarchyNodeType.TEST_GROUP)
		.flatMap((group) => {
			const components: React.ReactNode[] = [];

			// Render selected TESTs
			const testSummaries = [];

			for (const child of group.children) {
				if (child.nodeType === TestHierarchyNodeType.TEST) {
					testSummaries.push(renderTestNodeSummary(child));
				} else if (
					child.nodeType === TestHierarchyNodeType.TEST_GROUP_OPTION_RADIO_GROUP
				) {
					components.push(
						<div
							key={`${group.nodeId}-method`}
							className={`new-sample-info-row ${typographyClassname}`}
						>
							<div className="sample-info-row-description">{`${child.nodeName}: `}</div>
							<div className="wrap-text">{child.children[0].nodeName}</div>
						</div>,
					);
				}
			}

			if (testSummaries.length > 0) {
				components.push(
					<div
						key={`${group.nodeId}-tests`}
						className={`new-sample-info-row ${typographyClassname}`}
					>
						<div className="sample-info-row-description">{`${group.nodeName}: `}</div>
						<div className="wrap-text">{testSummaries.join(", ")}</div>
					</div>,
				);
			}
			const allDescendantNodes: Array<TestHierarchyTreeNode> =
				getAllDescendants(group);
			const descendantHierarchyNodeIds: Array<string> = allDescendantNodes.map(
				(node) => {
					return node.nodeId;
				},
			);

			const expectationGroupedByPackage: Map<
				string,
				Array<FrontendCompanyTestResultField>
			> = new Map();

			// 1. Sort the source array first so that the items added to the Map maintain display order
			const sortedFields = [...fieldsHavingExpectations].sort(
				(a, b) => a.displayOrder - b.displayOrder,
			);

			for (const field of sortedFields) {
				// 2. Find packages that match this field AND satisfy the hierarchy requirements
				const matchingPackages: Array<FrontendCompanyResultFieldHierarchyNodeMappingPackage> =
					hierarchyNodeMappingPackages.filter(
						(pkg) =>
							field.nodeMappingPackageIds.includes(pkg.packageId) &&
							pkg.mappingNodeIds.every((id) =>
								descendantHierarchyNodeIds.includes(id),
							),
					);

				// 3. If there are matches, group the field under each applicable package name
				for (const pkg of matchingPackages) {
					const existing = expectationGroupedByPackage.get(pkg.packageName);
					if (!existing) {
						expectationGroupedByPackage.set(pkg.packageName, [field]);
					} else {
						// Prevent duplicates if a field is somehow mapped twice to the same package name
						if (!existing.some((f) => f.fieldId === field.fieldId)) {
							existing.push(field);
						}
					}
				}
			}

			for (const [packageName, fields] of Array.from(
				expectationGroupedByPackage.entries(),
			)) {
				if (fields.length > 0) {
					const expectationsToDisplay: Array<React.ReactNode> = fields
						.map((field) => {
							const expectationField: FrontendCompanyTestExpectationField =
								field.expectationField as FrontendCompanyTestExpectationField;

							const expectation =
								expectationInputs[expectationField.expectationFieldId];
							if (!expectation) return null;

							const unit: string =
								companyTestResultUnits.find((unit) => {
									return unit.unitId == expectation.unitId;
								})?.unit || "";

							let displayValue = "";
							if (expectation.type === "value") {
								displayValue = expectation.value;
							} else if (expectation.type === "range") {
								if (expectation.min && expectation.max) {
									displayValue = `${expectation.min}${unit} ~ ${expectation.max}${unit}`;
								} else if (expectation.min) {
									displayValue = `≥ ${expectation.min}${unit}`;
								} else if (expectation.max) {
									displayValue = `≤ ${expectation.max}${unit}`;
								}
							}

							const fieldName =
								expectationFieldIdToNameMap.get(
									expectationField.expectationFieldId,
								) ?? field.fieldName;

							return (
								<div
									key={expectationField.expectationFieldId}
									className="typography-body-02"
									style={{
										display: "flex",
										alignItems: "center",
										gap: "6px",
										color: `${NeutralColor.NEUTRAL_GREY_500}`,
									}}
								>
									<span>{fieldName}</span>
									<span
										style={{
											background: `${PrimaryColor.PRIMARY_50}`,
											border: `1px solid ${PrimaryColor.PRIMARY_500}`,
											borderRadius: "4px",
											padding: "2px 8px",
										}}
									>
										{displayValue}
									</span>
								</div>
							);
						})
						.filter((node) => {
							return node != null;
						});
					if (expectationsToDisplay.length > 0) {
						components.push(
							<div
								key={`${packageName}-expectation-group`}
								className="expectation-group"
								style={{
									backgroundColor: "#F7F7F8",
									borderRadius: "8px",
									padding: "12px 16px",
									margin: "6px 0px",
								}}
							>
								<div
									className="expectation-group-title"
									style={{
										fontWeight: 500,
										marginBottom: "8px",
									}}
								>
									{`${packageName} Expectation:`}
								</div>

								<div
									className="expectation-fields"
									style={{
										display: "flex",
										flexWrap: "wrap",
										gap: "12px",
									}}
								>
									{expectationsToDisplay}
								</div>
							</div>,
						);
					}
				}
			}

			return components;
		});

	return result;
}

export function renderTestNodeSummary(testNode: TestHierarchyTreeNode): string {
	const base = testNode.nodeName;
	const radio = getSelectedRadioOption(testNode);
	const additional = getSelectedAdditionalOptions(testNode);
	let suffix = "";

	if (radio) suffix += ` (${radio})`;
	if (additional.length) suffix += ` ${additional.join(" ")}`;

	return base + suffix;
}

function getSelectedRadioOption(
	testNode: TestHierarchyTreeNode,
): string | null {
	const radioGroup = testNode.children.find(
		(child) => child.nodeType === TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP,
	);
	if (!radioGroup) return null;

	const selected = radioGroup.children.find(isSelectableTestNode);
	return selected?.nodeName ?? null;
}

function getSelectedAdditionalOptions(
	testNode: TestHierarchyTreeNode,
): string[] {
	const additionalGroup = testNode.children.find(
		(child) =>
			child.nodeType === TestHierarchyNodeType.TEST_ADDITIONAL_OPTION_GROUP,
	);
	if (!additionalGroup) return [];

	return additionalGroup.children
		.filter(isSelectableTestNode)
		.map((child) => `(+ ${child.nodeName})`);
}

function getAllDescendants(
	node: TestHierarchyTreeNode,
): Array<TestHierarchyTreeNode> {
	const descendants: Array<TestHierarchyTreeNode> = [];

	for (const child of node.children) {
		descendants.push(child);
		descendants.push(...getAllDescendants(child));
	}

	return descendants;
}
